import React, { useState } from 'react';
import { BellRing, BellOff, Bell, Share } from 'lucide-react';
import { useNotificationPermission } from '../../hooks/useNotificationPermission';
import { describePushStatus, isIOS, isStandalone } from '../../utils/pushSupport';
import { isWebNotifyEnabled, setWebNotifyEnabled } from '../../utils/webNotifyPref';
import { subscribeToPush, unsubscribeFromPush } from '../../services/webPushService';
import { ToggleSwitch } from '../ToggleSwitch';

/**
 * 웹/PWA 알림 상태 + 켜기 진입점 + "이 기기에서 알림 받기" 토글.
 *
 * 이 화면이 생기기 전에는 웹 사용자가 권한 배너를 한 번 닫거나(7일 쿨다운) 권한이 차단되면
 * 앱 안에서 알림을 되살릴 길이 아예 없었다. PC 앱에는 트레이 토글이 있는데 웹에는 대응물이 없었다.
 * 기기별 끄기 토글(webNotifyPref)이 그 트레이 토글의 웹 대응물이다 — 끄면 이 기기의 푸시 구독을
 * 해제하고(다른 기기는 endpoint 단위라 영향 없음) 페이지 알림도 억제한다.
 *
 * Tauri에서는 마운트하지 않는다 — 그쪽 최상위 스위치는 트레이의 "알림 받기"다.
 */
export const NotificationSettingsPanel: React.FC = () => {
  const { permission, capability, refresh } = useNotificationPermission();
  const [appEnabled, setAppEnabled] = useState(isWebNotifyEnabled);
  const info = describePushStatus(capability, permission, appEnabled);

  const handleEnable = () => {
    // ⚠ 클릭 핸들러 첫 줄에서 동기적으로 호출한다. 앞에 await를 두면 iOS에서
    //    transient activation이 만료돼 권한 프롬프트가 조용히 무시된다.
    try {
      Notification.requestPermission()
        .then((result) => {
          refresh();
          if (result === 'granted') void subscribeToPush();
        })
        .catch((err) => console.warn('[Notification] Permission request failed:', err));
    } catch (err) {
      console.warn('[Notification] Permission request failed:', err);
    }
  };

  // 이 기기 알림 토글. 끄기 = 플래그 + 푸시 구독 해제(서버 행 삭제), 켜기 = 플래그 복원 + 재구독.
  // 플래그를 먼저 되돌려야 subscribeToPush() 첫 줄 가드에 걸리지 않는다.
  const handleDeviceToggle = (next: boolean) => {
    setWebNotifyEnabled(next);
    setAppEnabled(next);
    if (!next) {
      void unsubscribeFromPush();
      return;
    }
    if (permission === 'granted') {
      void subscribeToPush();
    } else if (permission === 'default') {
      handleEnable(); // 동기 requestPermission — iOS transient activation 규칙 유지
    }
    // denied면 아래 '차단됨' 안내가 이어서 보인다 — 브라우저에서 허용해야 실제로 켜진다
  };

  const Icon = !appEnabled || permission === 'denied'
    ? BellOff
    : permission === 'granted' ? BellRing : Bell;
  const tone =
    appEnabled && permission === 'granted'
      ? 'bg-brand-100 text-brand-700'
      : 'bg-slate-100 text-slate-600';

  return (
    <div className="space-y-3">
      <div className="flex items-start gap-3">
        <span className={`shrink-0 w-9 h-9 rounded-full grid place-items-center ${tone}`}>
          <Icon size={18} aria-hidden="true" />
        </span>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-slate-800">{info.headline}</p>
          <p className="text-xs text-slate-500 mt-1 leading-relaxed">{info.detail}</p>
        </div>
      </div>

      {/* 받을 수 없는 환경(설치 필요·미지원)에서는 끄기 스위치가 혼란만 준다 — 숨긴다.
          denied에서는 보여준다: 켜 두면 브라우저 허용 시 즉시 동작한다는 상태가 남는다 */}
      {capability === 'ok' && (
        <div className="border-t border-slate-100">
          <ToggleSwitch
            checked={appEnabled}
            onChange={handleDeviceToggle}
            label="이 기기에서 알림 받기"
            description="끄면 이 기기로만 알림이 오지 않습니다"
          />
        </div>
      )}

      {appEnabled && info.action === 'request' && (
        <button
          type="button"
          onClick={handleEnable}
          className="w-full px-3 py-2.5 bg-brand-600 text-white rounded-lg text-sm font-semibold hover:bg-brand-700 transition-colors"
        >
          알림 켜기
        </button>
      )}

      {info.action === 'install' && (
        <div className="rounded-lg bg-slate-50 border border-slate-200 px-3 py-2.5 text-xs text-slate-600 leading-relaxed">
          사파리 아래쪽 <Share size={13} className="inline text-brand-600" aria-hidden="true" /> 공유 버튼 →{' '}
          <strong className="text-slate-800">홈 화면에 추가</strong> → 홈 화면 아이콘으로 실행
        </div>
      )}

      {info.action === 'guide' && (
        <div className="rounded-lg bg-slate-50 border border-slate-200 px-3 py-2.5 text-xs text-slate-600 leading-relaxed space-y-1">
          {isIOS() && isStandalone() ? (
            <p>설정 앱 → 알림 → INA-TALK → 알림 허용을 켜 주세요.</p>
          ) : (
            <>
              <p>주소창 왼쪽 자물쇠 → 사이트 설정 → 알림 → 허용</p>
              <p className="text-slate-400">허용으로 바꾼 뒤 이 화면으로 돌아오면 상태가 갱신됩니다.</p>
            </>
          )}
        </div>
      )}

      {/* 아이폰은 앱이 완전히 종료된 상태에서 알림 표시 방식이 다르다 — 기대치를 미리 맞춘다. */}
      {appEnabled && isIOS() && permission === 'granted' && (
        <p className="text-xs text-slate-400 leading-relaxed">
          아이폰은 앱이 완전히 꺼져 있으면 전화 알림이 짧게만 표시됩니다. 통화가 잦다면 PC 앱을 함께 사용해 주세요.
        </p>
      )}
    </div>
  );
};
